import { CBadge, CTooltip, CButton, CSpinner } from '@coreui/react';
import { defineCol, formatCurrency, formatPercent, plClass } from '@trading/ui-table';
import type { ColumnDef, FilterDef } from '@trading/ui-table';
import { dailyMoveVsAtr } from './atr.js';
import { TargetOverrideCell } from './TargetOverrideCell.js';
import { EntryDateOverrideCell } from './EntryDateOverrideCell.js';
import type { DecisionKind, PositionRow } from './types.js';

const HIGH_ATR_PCT_THRESHOLD = 5;

const TARGET_SOURCE_LABEL: Record<string, string> = {
    manual: 'Manual',
    tradingview_leg: 'TV Leg',
    atr_default: 'ATR',
};

const TARGET_SOURCE_COLOR: Record<string, string> = {
    manual: 'info',
    tradingview_leg: 'primary',
    atr_default: 'secondary',
};

// Weekly bars -> trading days, matching trading_server's
// target/constants.ts#TRADING_DAYS_PER_WEEK (display-only reverse-math here,
// not a source of truth).
const TRADING_DAYS_PER_WEEK = 5;

/** Per-row breakdown shown on hover: where estimatedDays came from and how it nets out to the displayed days-to-target/overdue figure. */
const daysToTargetTooltipContent = (row: PositionRow) => {
    const { estimatedDays, daysHeld, daysToTarget, daysOverdue, targetSource } = row;
    if (estimatedDays == null) return 'Not enough data yet to estimate a target timeline.';

    const held = daysHeld ?? 0;
    const sourceLine =
        targetSource === 'tradingview_leg'
            ? `Source: TradingView leg data — avg leg took ~${(estimatedDays / TRADING_DAYS_PER_WEEK).toFixed(1)} weekly bars (× ${TRADING_DAYS_PER_WEEK} trading days/week = ${estimatedDays}d)`
            : `Source: no TradingView-leg data for this symbol — flat ${estimatedDays}-day default`;
    const resultLine =
        daysOverdue != null && daysOverdue > 0
            ? `${estimatedDays}d estimate − ${held}d held = ${daysOverdue}d overdue`
            : `${estimatedDays}d estimate − ${held}d held = ${daysToTarget ?? 0}d remaining`;

    return (
        <>
            {sourceLine}
            <br />
            {resultLine}
        </>
    );
};

const FLAG_LABELS = {
    bookProfit: 'Book profit',
    stopLossBreached: 'Stop-loss breached',
    holdingTimeFlagged: 'Review hold',
    deadTradeFlagged: 'Dead trade',
} as const;

/**
 * Every alert flag currently active on this position, in a stable display order.
 *
 * `stopLossBreached` and the `cut_loss` decision are the same underlying signal (decidePosition
 * defers to the server's verdict when it has one), so only the flag is rendered — never both.
 */
export const effectiveDecisionKind = (row: PositionRow): DecisionKind => {
    // An explicit server verdict outranks the decision's own client-side guess, in both
    // directions, so the Flags badge and the row tint can never contradict each other.
    if (row.stopLossBreached === true) return 'cut_loss';
    if (row.stopLossBreached === false && row.decision.kind === 'cut_loss') return 'stay';
    return row.decision.kind;
};

const activeFlags = (row: PositionRow): string[] => {
    const kind = effectiveDecisionKind(row);
    const flags: string[] = [];
    if (kind === 'book_profit') flags.push(FLAG_LABELS.bookProfit);
    if (kind === 'cut_loss') flags.push(FLAG_LABELS.stopLossBreached);
    if (row.holdingTimeFlagged) flags.push(FLAG_LABELS.holdingTimeFlagged);
    if (row.deadTradeFlagged) flags.push(FLAG_LABELS.deadTradeFlagged);
    return flags;
};

/**
 * Row tint carried over from the pre-shared-table positions view: a decisive row is strongly
 * tinted, everything else gets a faint P&L wash.
 */
export const decisionRowStyle = (row: PositionRow): { backgroundColor: string } => {
    const kind = effectiveDecisionKind(row);
    if (kind === 'cut_loss') return { backgroundColor: 'rgba(220, 53, 69, 0.18)' };
    if (kind === 'book_profit') return { backgroundColor: 'rgba(25, 135, 84, 0.18)' };
    return row.unrealizedPl >= 0
        ? { backgroundColor: 'rgba(25, 135, 84, 0.06)' }
        : { backgroundColor: 'rgba(220, 53, 69, 0.06)' };
};

/** `…` while the ATR cache is still being computed, `—` once we know there is no value. */
const atrPlaceholder = (atrLoading: boolean): string => (atrLoading ? '…' : '—');

const atr14Col = (atrLoading: boolean): ColumnDef<PositionRow> =>
    defineCol({
        key: 'atr14',
        label: 'ATR(14)$',
        accessor: (row) => row.atr14,
        type: 'num',
        render(value) {
            if (value == null) return <span>{atrPlaceholder(atrLoading)}</span>;
            return <span>{formatCurrency(value)}</span>;
        },
    });

const atrPctCol = (atrLoading: boolean): ColumnDef<PositionRow> =>
    defineCol({
        key: 'atrPct',
        label: 'ATR%',
        accessor: (row) => row.atrPct,
        type: 'num',
        render(value) {
            if (value == null) return <span>{atrPlaceholder(atrLoading)}</span>;
            const className = value > HIGH_ATR_PCT_THRESHOLD ? 'text-yellow' : undefined;
            return <span className={className}>{value.toFixed(2)}%</span>;
        },
    });

export const POSITIONS_COLUMNS: ColumnDef<PositionRow>[] = [
    defineCol({
        key: 'symbol',
        label: 'Symbol',
        accessor: (row) => row.symbol,
        type: 'text',
        render(value, row) {
            const dailyMove = dailyMoveVsAtr(row.currentPrice, row.atr14, row.prevDayClose);
            return (
                <span className="d-flex align-items-center gap-1">
                    <strong>{value}</strong>
                    {dailyMove === 'up' && (
                        <CBadge color="success" shape="rounded-pill" title="Moved up >1x ATR today">
                            ▲1ATR
                        </CBadge>
                    )}
                    {dailyMove === 'down' && (
                        <CBadge
                            color="danger"
                            shape="rounded-pill"
                            title="Moved down >1x ATR today"
                        >
                            ▼1ATR
                        </CBadge>
                    )}
                </span>
            );
        },
    }),
    defineCol({
        key: 'flags',
        label: 'Flags',
        description:
            'Book-profit, stop-loss breach, hold-time review, and dead-trade alerts for this position',
        accessor: (row) => activeFlags(row).join(',') || null,
        type: 'text',
        render(_value, row) {
            const flags = activeFlags(row);
            if (flags.length === 0) return <span className="text-dim">—</span>;
            return (
                <span className="d-flex align-items-center gap-1 flex-wrap">
                    {flags.includes(FLAG_LABELS.bookProfit) && (
                        <CTooltip content={row.decision.reasons.join('; ')} placement="top">
                            <CBadge color="success" shape="rounded-pill">
                                {FLAG_LABELS.bookProfit}
                            </CBadge>
                        </CTooltip>
                    )}
                    {flags.includes(FLAG_LABELS.stopLossBreached) && (
                        <CTooltip
                            content={
                                row.stopLevel != null
                                    ? `Price is below the server-computed stop level ($${row.stopLevel.toFixed(2)})`
                                    : row.decision.reasons.join('; ')
                            }
                            placement="top"
                        >
                            <CBadge color="danger" shape="rounded-pill">
                                {FLAG_LABELS.stopLossBreached}
                            </CBadge>
                        </CTooltip>
                    )}
                    {row.holdingTimeFlagged && (
                        <CTooltip
                            content="Held longer than 0.8x the current average winning hold time — consider a review"
                            placement="top"
                        >
                            <CBadge color="warning" shape="rounded-pill">
                                {FLAG_LABELS.holdingTimeFlagged}
                            </CBadge>
                        </CTooltip>
                    )}
                    {row.deadTradeFlagged && (
                        <CTooltip
                            content="Sat within ±0.5% P&L for past 50% of this position's own estimated days-to-target — consider a sideways exit"
                            placement="top"
                        >
                            <CBadge color="secondary" shape="rounded-pill">
                                {FLAG_LABELS.deadTradeFlagged}
                            </CBadge>
                        </CTooltip>
                    )}
                </span>
            );
        },
    }),
    defineCol({
        key: 'side',
        label: 'Side',
        accessor: (row) => row.side,
        type: 'text',
        render(value) {
            return <CBadge color={value === 'long' ? 'success' : 'danger'}>{value}</CBadge>;
        },
    }),
    defineCol({
        key: 'qty',
        label: 'Qty',
        accessor: (row) => row.qty,
        type: 'num',
        formatter: (value) => (value == null ? '—' : value.toLocaleString()),
    }),
    defineCol({
        key: 'entryPrice',
        label: 'Entry',
        description:
            "Broker average entry price, or the consumer's recorded buying price when it has one",
        accessor: (row) => row.entryPrice,
        type: 'num',
        formatter: (value) => formatCurrency(value),
    }),
    defineCol({
        key: 'currentPrice',
        label: 'Current Price',
        accessor: (row) => row.currentPrice,
        type: 'num',
        formatter: (value) => formatCurrency(value),
    }),
    defineCol({
        key: 'marketValue',
        label: 'Market Value',
        accessor: (row) => row.marketValue,
        type: 'num',
        formatter: (value) => formatCurrency(value),
    }),
    defineCol({
        key: 'unrealizedPl',
        label: 'P&L $',
        accessor: (row) => row.unrealizedPl,
        type: 'num',
        render(value) {
            if (value == null) return <span>—</span>;
            return <span className={plClass(value)}>{formatCurrency(value)}</span>;
        },
    }),
    defineCol({
        key: 'unrealizedPlPct',
        label: 'P&L %',
        accessor: (row) => row.unrealizedPlPct,
        type: 'num',
        render(value) {
            if (value == null) return <span>—</span>;
            return <span className={plClass(value)}>{formatPercent(value)}</span>;
        },
    }),
    defineCol({
        key: 'daysHeld',
        label: 'Days Held',
        description: 'Days since entryDate',
        accessor: (row) => row.daysHeld,
        type: 'num',
        formatter: (value) => (value == null ? '—' : String(value)),
    }),
    atr14Col(false),
    atrPctCol(false),
    defineCol({
        key: 'upsidePct',
        label: 'Expected Upside',
        description: 'Expected upside % from analysis — what this trade was entered for',
        accessor: (row) => row.upsidePct,
        type: 'num',
        render(value) {
            if (value == null) return <span>—</span>;
            return <span>{formatPercent(value)}</span>;
        },
    }),
    defineCol({
        key: 'target',
        label: 'Target',
        description: 'Manual override, else TradingView-leg data, else entry + 10x ATR14',
        accessor: (row) => row.target,
        type: 'num',
        render(value, row) {
            if (value == null) return <span>—</span>;
            return (
                <span className="d-flex align-items-center gap-1">
                    <span>{formatCurrency(value)}</span>
                    {row.targetSource && (
                        <CBadge color={TARGET_SOURCE_COLOR[row.targetSource]} shape="rounded-pill">
                            {TARGET_SOURCE_LABEL[row.targetSource]}
                        </CBadge>
                    )}
                </span>
            );
        },
    }),
    defineCol({
        key: 'upsideRemainingPct',
        label: 'Upside Remaining',
        description:
            '(target - currentPrice) / currentPrice * 100 — negative once price has moved past the target',
        accessor: (row) => row.upsideRemainingPct,
        type: 'num',
        render(value) {
            if (value == null) return <span>—</span>;
            return <span className={plClass(value)}>{formatPercent(value)}</span>;
        },
    }),
    defineCol({
        key: 'daysToTarget',
        label: 'Days to Target',
        description:
            'max(0, estimatedDays - daysHeld) — estimatedDays comes from the TradingView-leg pipeline when available (any age), else a flat 15-day default. Once daysHeld exceeds the estimate, shows how many days overdue instead of clamping at 0.',
        accessor: (row) => row.daysToTarget,
        type: 'num',
        render(value, row) {
            const tooltip = daysToTargetTooltipContent(row);
            if (row.daysOverdue != null && row.daysOverdue > 0) {
                return (
                    <CTooltip content={tooltip} placement="top">
                        <span className="text-yellow">Overdue {row.daysOverdue}d</span>
                    </CTooltip>
                );
            }
            return (
                <CTooltip content={tooltip} placement="top">
                    <span>{value == null ? '—' : String(value)}</span>
                </CTooltip>
            );
        },
    }),
];

export const POSITIONS_FILTERS: FilterDef<PositionRow>[] = [
    {
        key: 'symbol',
        type: 'text',
        placeholder: 'Symbol…',
        accessor: (row) => row.symbol,
    },
    {
        key: 'flags',
        type: 'multiselect',
        placeholder: 'All flags',
        accessor: (row) => activeFlags(row).join(','),
        options: Object.values(FLAG_LABELS),
    },
];

export interface PositionsColumnActions {
    /** Omit to render the Entry Date column as read-only text. */
    onSetEntryDate?: (symbol: string, entryDate: string | null) => void;
    /** Omit to hide the Set Target column entirely. */
    onSetTarget?: (symbol: string, target: number | null) => void;
    /** Omit to hide the Close column entirely. */
    onClose?: (row: PositionRow) => void;
    closingSymbol?: string | null;
    pendingCloseSymbols?: ReadonlySet<string>;
    /** Renders `…` instead of `—` in the ATR cells while the ATR cache is still loading. */
    atrLoading?: boolean;
}

/**
 * Splices the interactive columns into POSITIONS_COLUMNS. Each one appears only when its
 * callback is supplied, so a read-only consumer (e.g. the position-app PWA, which has no
 * override endpoints) gets the same table minus the editors.
 */
export const buildPositionsColumns = ({
    onSetEntryDate,
    onSetTarget,
    onClose,
    closingSymbol = null,
    pendingCloseSymbols,
    atrLoading = false,
}: PositionsColumnActions): ColumnDef<PositionRow>[] => {
    const entryDateCol: ColumnDef<PositionRow> = {
        key: 'entryDate',
        label: 'Entry Date',
        description:
            'Manual override, else reconstructed from Alpaca fill history, else the date this position was first observed',
        accessor: (row) => row.entryDate,
        type: 'text',
        render(_value, row) {
            if (!onSetEntryDate) {
                return <span>{row.entryDate ?? '—'}</span>;
            }
            return <EntryDateOverrideCell row={row} onSetOverride={onSetEntryDate} />;
        },
    };

    const base = atrLoading
        ? POSITIONS_COLUMNS.map((col) =>
              col.key === 'atr14' ? atr14Col(true) : col.key === 'atrPct' ? atrPctCol(true) : col,
          )
        : POSITIONS_COLUMNS;

    const plPctIndex = base.findIndex((c) => c.key === 'unrealizedPlPct');
    const columns = [...base.slice(0, plPctIndex + 1), entryDateCol, ...base.slice(plPctIndex + 1)];

    if (onSetTarget) {
        columns.push({
            key: 'targetOverride',
            label: 'Set Target',
            type: 'text',
            sortable: false,
            filterable: false,
            accessor: () => null,
            render(_value, row) {
                return <TargetOverrideCell row={row} onSetOverride={onSetTarget} />;
            },
        });
    }

    if (onClose) {
        columns.push({
            key: 'actions',
            label: '',
            type: 'text',
            sortable: false,
            filterable: false,
            accessor: () => null,
            render(_value, row) {
                const isClosing = closingSymbol === row.symbol;
                const isPending = pendingCloseSymbols?.has(row.symbol) ?? false;
                return (
                    <CButton
                        color="danger"
                        size="sm"
                        variant="outline"
                        disabled={isClosing || isPending}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose(row);
                        }}
                        title={
                            isPending
                                ? 'Close order submitted — waiting for fill'
                                : `Close all ${row.qty} shares at market`
                        }
                    >
                        {isClosing ? <CSpinner size="sm" /> : isPending ? 'Pending' : 'Close'}
                    </CButton>
                );
            },
        });
    }

    return columns;
};
